import type Badge from '$lib/components/atoms/badge/Badge.svelte';
import type {
	AccommodationMichelinPoiData,
	AccommodationPoiData,
	GasStationPoiData,
	ParkingPoiData,
	Partner1PoiData,
	Partner2PoiData,
	PicturePoiCardScheme,
	PoiData,
	PoiFilterOptionId,
	RestaurantMichelinPoiData,
	RestaurantPoiData,
	RestStopPoiData,
	ServiceEVPoiData,
	TourismPoiData,
} from '$lib/components/pois/types';
import type { ComponentProps } from 'svelte';
import type { nonHRTBadgeColor } from './constant';
import type { PoiCardFactory, PoiCardListScheme, PoiCardSize } from './types';
import { getSponsorConfigByPoi } from '$lib/constants/sponsoredPoi';
import { cardScheme } from './constant';
import {
	buildAccommodationDistinctions,
	buildAccommodationMichelinDistinction,
	buildAccommodationPriceTag,
	buildAccomodationsFooterLink,
	buildGasStationBadgeDot,
	buildGasStationContent,
	buildHeroBadge,
	buildNonHRTPoisBadge,
	buildPoiHRTImageOrCarousel,
	buildPriceTagPriceRange,
	buildRestaurantMichelinDistinctions,
	buildRestaurantPriceTag,
	buildRestStopHighlight,
	buildRestStopSubtitle,
	buildScoreBadge,
	buildTheForkFooter,
	buildTourismDistinctions,
	getFuelsAndGasStationFiltersAndLastUpdated,
} from './factories-utils';

type Overrides = Partial<PoiCardFactory>;

interface FactoryArgs<T> {
	poi: T;
	href: string;
	size: PoiCardSize;
	index: number;
	scheme: PoiCardListScheme;
	overrides?: Overrides;
}

export function buildPoiCardProps(poi: PoiData, href: string, index: number, size: PoiCardSize = 'lg', overrides?: Overrides): PoiCardFactory | undefined {
	const scheme = cardScheme[poi.__typename as Exclude<PoiData['__typename'], 'PartnerPOI' | 'Partner2POI'>];

	switch (poi.__typename) {
		case 'AccommodationPOI':
			return accommodationPoiFactory({ poi, href, size, index, scheme, overrides });
		case 'AccommodationMichelinPOI':
			return accommodationMichelinPoiFactory({ poi, href, size, index, scheme, overrides });
		case 'RestaurantPOI':
			return restaurantPoiFactory({ poi, href, size, index, scheme, overrides });
		case 'RestaurantMichelinPOI':
			return restaurantMichelinPoiFactory({ poi, href, size, index, scheme, overrides });
		case 'TourismPOI':
			return tourismPoiFactory({ poi, href, size, index, scheme, overrides });
		case 'ServiceEVPOI':
			return evStationFactory({ poi, href, size, index, scheme, overrides });
		case 'ServiceGasStationPOI':
			return gasStationFactory({ poi, href, size, index, scheme, overrides });
		case 'ParkingPOI':
			return parkingFactory({ poi, href, size, index, scheme, overrides });
		case 'ServiceRestStopPOI':
			return restStopFactory({ poi, href, size, index, scheme, overrides });
		case 'PartnerPOI':
			return partnerPoiFactory({ poi, href, size, index, scheme, overrides });
		case 'Partner2POI':
			return partnerPoiFactory({ poi, href, size, index, scheme, overrides });
		default:
			return undefined;
	}
}

function accommodationPoiFactory({ poi, href, size, index, scheme, overrides }: FactoryArgs<AccommodationPoiData>) {
	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		hero: {
			...buildPoiHRTImageOrCarousel(poi, index, scheme as PicturePoiCardScheme),
			badge: buildHeroBadge(scheme as PicturePoiCardScheme),
			priceTag: buildAccommodationPriceTag(poi),
		},
		content: {
			title: {
				title: poi.name,
				badge: buildScoreBadge(poi),
			},
			city: poi?.address?.city ?? undefined,
			distinction: buildAccommodationDistinctions(poi, scheme as PicturePoiCardScheme),
		},
		footer: {
			link: buildAccomodationsFooterLink(poi, scheme as PicturePoiCardScheme),
		},
		...overrides,
	} satisfies PoiCardFactory;
}

function partnerPoiFactory({ poi, href, size, index, scheme, overrides }: FactoryArgs<Partner1PoiData | Partner2PoiData>) {
	const sponsoredPoiConfig = getSponsorConfigByPoi(poi);
	const badge = {
		imgUrl: sponsoredPoiConfig?.badge.imgUrl,
		isSquare: sponsoredPoiConfig?.badge.isSquare,
		colorStyles: sponsoredPoiConfig?.badge.style,
		iconPosition: 'right',
	} satisfies ComponentProps<typeof Badge>;

	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		content: {
			title: {
				title: poi.name,
				badge,
			},
			city: poi?.address?.city ?? undefined,
		},
		...overrides,
	} satisfies PoiCardFactory;
}

function accommodationMichelinPoiFactory({ poi, href, size, index, scheme, overrides }: FactoryArgs<AccommodationMichelinPoiData>) {
	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		hero: {
			...buildPoiHRTImageOrCarousel(poi, index, scheme as PicturePoiCardScheme),
			badge: buildHeroBadge(scheme as PicturePoiCardScheme),
			priceTag: buildAccommodationPriceTag(poi),
		},
		content: {
			title: {
				title: poi.name,
				badge: buildScoreBadge(poi),
			},
			city: poi?.address?.city ?? undefined,
			distinction: buildAccommodationMichelinDistinction(poi, scheme as PicturePoiCardScheme),
		},
		footer: {
			link: buildAccomodationsFooterLink(poi, scheme as PicturePoiCardScheme),
		},
		...overrides,
	} satisfies PoiCardFactory;
}

function restaurantMichelinPoiFactory({ poi, href, size, index, scheme, overrides }: FactoryArgs<RestaurantMichelinPoiData>) {
	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		hero: {
			...buildPoiHRTImageOrCarousel(poi, index, scheme as PicturePoiCardScheme),
			badge: buildHeroBadge(scheme as PicturePoiCardScheme),
			priceTag: {
				priceRange: buildPriceTagPriceRange(poi),
			},
		},
		content: {
			title: {
				title: poi.name,
			},
			city: poi?.address?.city ?? undefined,
			distinction: buildRestaurantMichelinDistinctions(poi),
		},
		footer: {
			theFork: buildTheForkFooter(poi),
		},
		...overrides,
	} satisfies PoiCardFactory;
}

function restaurantPoiFactory({ poi, href, size, index, scheme, overrides }: FactoryArgs<RestaurantPoiData>) {
	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		hero: {
			...buildPoiHRTImageOrCarousel(poi, index, scheme as PicturePoiCardScheme),
			badge: buildHeroBadge(scheme as PicturePoiCardScheme),
			priceTag: buildRestaurantPriceTag(poi.averagePrice),
		},
		content: {
			title: {
				title: poi.name,
				badge: buildScoreBadge(poi),
			},
			city: poi?.address?.city ?? undefined,
		},
		footer: {
			theFork: buildTheForkFooter(poi),
		},
		...overrides,
	} satisfies PoiCardFactory;
}

function tourismPoiFactory({ poi, href, size, index, scheme, overrides }: FactoryArgs<TourismPoiData>) {
	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		hero: {
			...buildPoiHRTImageOrCarousel(poi, index, scheme as PicturePoiCardScheme),
			badge: buildHeroBadge(scheme as PicturePoiCardScheme),
		},
		content: {
			title: {
				title: poi.name,
			},
			city: poi?.address?.city ?? undefined,
			distinction: buildTourismDistinctions(poi, scheme as PicturePoiCardScheme),
		},
		...overrides,
	} satisfies PoiCardFactory;
}

function evStationFactory({ poi, href, index, size, scheme, overrides }: FactoryArgs<ServiceEVPoiData>) {
	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		content: {
			title: {
				title: poi.name,
				clamp: 'truncate',
				badge: buildNonHRTPoisBadge(scheme as keyof typeof nonHRTBadgeColor),
			},
			city: poi?.address?.city ?? undefined,
			evStation: {
				spotsCount: poi.spots_count,
				connectors: poi.connectors,
			},
		},
		...overrides,
	} satisfies PoiCardFactory;
}

function gasStationFactory({ poi, href, index, size, scheme, overrides }: FactoryArgs<GasStationPoiData>) {
	const { fuelPricesMap, gasStationTypeFilter, lastUpdated } = getFuelsAndGasStationFiltersAndLastUpdated(poi);
	const { class: cls, ...rest } = buildNonHRTPoisBadge(scheme as keyof typeof nonHRTBadgeColor);
	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		content: {
			title: {
				title: poi.name,
				badge: {
					...rest,
					class: `${cls} ${buildGasStationBadgeDot(fuelPricesMap, gasStationTypeFilter as unknown as PoiFilterOptionId[])}`,
				},
			},
			city: poi?.address?.city ?? poi?.address?.formattedAddress ?? undefined,
			gasStation: buildGasStationContent(fuelPricesMap, gasStationTypeFilter as unknown as PoiFilterOptionId[], lastUpdated),
		},
		...overrides,
	} satisfies PoiCardFactory;
}

function parkingFactory({ poi, href, index, size, scheme, overrides }: FactoryArgs<ParkingPoiData>) {
	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		content: {
			title: {
				title: poi.name,
				badge: buildNonHRTPoisBadge(scheme as keyof typeof nonHRTBadgeColor),
			},
			city: poi?.address?.city ?? undefined,
			parking: {
				address: poi.splittedAddress,
			},
		},
		...overrides,
	} satisfies PoiCardFactory;
}

function restStopFactory({ poi, href, index, size, scheme, overrides }: FactoryArgs<RestStopPoiData>) {
	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		content: {
			title: {
				title: poi.name,
				clamp: 'truncate',
				badge: buildNonHRTPoisBadge(scheme as keyof typeof nonHRTBadgeColor),
			},
			city: poi?.address?.city ?? undefined,
			restStop: {
				...buildRestStopSubtitle(poi),
				...buildRestStopHighlight(poi),
			},
		},
		...overrides,
	} satisfies PoiCardFactory;
}
